<script setup lang="ts">
/**
 * StepUpDialog — the one modal that asks for an authenticator code.
 *
 * Mounted once in the app shell; `useStepUp()` drives it from anywhere. plan-v2 §4 makes the
 * shape of it explicit, and the shape is asymmetric on purpose:
 *
 *  · **Enabling a protection** gets a short confirm. Nobody is harmed by a protection
 *    appearing, so the dialog's job is only to say what will now ask for a code.
 *  · **Disabling one** states the consequence in full, names who will be emailed about it, and
 *    takes the code HERE. The step-up and the confirmation are one act, which is what a stolen
 *    session cannot complete — a dialog that only said "are you sure?" could be clicked
 *    through by whoever has the browser.
 *
 * The un-enrolled case is its own panel rather than an error on the form, because the answer
 * is not "try again", it is "there is nothing to try with yet".
 */
import { ref, watch, nextTick } from 'vue'
import AppModal from '@/components/ui/AppModal.vue'
import { useStepUp } from '@/composables/useStepUp'

const { open, prompt, busy, error, notEnrolled, submitCode, confirmWithoutCode, cancel } = useStepUp()

const code = ref('')
const input = ref<HTMLInputElement | null>(null)

watch(open, async isOpen => {
  if (!isOpen) { code.value = ''; return }
  // Focus the field, so the common case is: dialog appears, type six digits, Enter.
  await nextTick()
  input.value?.focus()
})

function onSubmit() {
  if (!code.value.trim() || busy.value) return
  void submitCode(code.value)
}
</script>

<template>
  <AppModal
    :open="open"
    :title="prompt?.title ?? 'Confirm with your authenticator'"
    size="sm"
    :close-on-backdrop="false"
    @close="cancel"
  >
    <!-- Nothing to verify with. The way out is to enrol, not to retry. -->
    <div v-if="notEnrolled" class="su">
      <p class="su__lead">
        This action needs two-factor authentication, and your account does not have it set up yet.
      </p>
      <p class="su__hint">
        Set it up under <strong>Settings → Security</strong>, then come back to this.
        It takes about a minute and needs an authenticator app on your phone.
      </p>
    </div>

    <form v-else class="su" @submit.prevent="onSubmit">
      <p v-if="prompt?.consequence" class="su__consequence" :class="{ 'su__consequence--danger': prompt?.danger }">
        {{ prompt.consequence }}
      </p>

      <template v-if="prompt?.requireCode !== false">
        <label class="su__label" for="stepup-code">Authenticator code</label>
        <input
          id="stepup-code"
          ref="input"
          v-model="code"
          class="input su__code"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          placeholder="000000"
          maxlength="20"
          :disabled="busy"
        />
        <p class="su__hint">
          The six-digit code your authenticator app is showing now. A recovery code works here too.
        </p>
      </template>

      <p v-if="prompt?.notify" class="su__notify">
        <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h16v12H4z"/><path d="m4 7 8 6 8-6"/></svg>
        {{ prompt.notify }}
      </p>

      <p v-if="error" class="su__error">{{ error }}</p>
    </form>

    <template #footer>
      <button class="btn" type="button" @click="cancel">{{ notEnrolled ? 'Close' : 'Cancel' }}</button>
      <button
        v-if="!notEnrolled && prompt?.requireCode === false"
        class="btn btn-primary"
        type="button"
        @click="confirmWithoutCode"
      >
        {{ prompt?.confirmLabel ?? 'Confirm' }}
      </button>
      <button
        v-else-if="!notEnrolled"
        class="btn"
        :class="prompt?.danger ? 'btn-danger' : 'btn-primary'"
        type="button"
        :disabled="busy || !code.trim()"
        @click="onSubmit"
      >
        {{ busy ? 'Checking…' : (prompt?.confirmLabel ?? 'Confirm') }}
      </button>
    </template>
  </AppModal>
</template>

<style scoped>
.su { display: flex; flex-direction: column; gap: 10px; }
.su__lead { font-size: 14px; color: var(--ink); margin: 0; }
.su__consequence {
  margin: 0; padding: 11px 13px; border-radius: var(--r-md);
  border: 1px solid var(--hairline); background: var(--surface-2);
  font-size: 13px; line-height: 1.5; color: var(--ink);
}
/* An unprotected money action must not look the same as a protected one. */
.su__consequence--danger { border-color: var(--bad); color: var(--bad); background: color-mix(in srgb, var(--bad) 8%, transparent); }
.su__label { font-size: 12px; font-weight: 600; color: var(--ink-muted); text-transform: uppercase; letter-spacing: 0.04em; }
.su__code { font-size: 20px; letter-spacing: 0.22em; text-align: center; font-variant-numeric: tabular-nums; }
.su__hint { margin: 0; font-size: 12px; color: var(--ink-muted); line-height: 1.45; }
.su__notify { display: flex; align-items: flex-start; gap: 8px; margin: 2px 0 0; font-size: 12px; color: var(--ink-muted); line-height: 1.45; }
.su__notify .ico { width: 15px; height: 15px; flex-shrink: 0; margin-top: 1px; }
.su__error { margin: 0; font-size: 13px; color: var(--bad); }
</style>
